import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from "react";

/** Native popovers escape the scrolling toolbar and dismiss on Escape/outside click. */
export function OverlaySettings({ label, children }: { label: string; children: ReactNode }) {
  const id = useId();
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const position = useCallback(() => {
    if (!button.current || !panel.current) return;
    const anchor = button.current.parentElement!.getBoundingClientRect();
    const width = Math.min(300, window.innerWidth - 24);
    panel.current.style.width = `${width}px`;
    panel.current.style.left = `${Math.max(12, Math.min(anchor.left, window.innerWidth - width - 12))}px`;
    panel.current.style.top = `${anchor.bottom + 6}px`;
    panel.current.style.maxHeight = `${Math.max(80, window.innerHeight - anchor.bottom - 18)}px`;
  }, []);
  useEffect(() => {
    if (!open) return;
    position();
    window.addEventListener("resize", position);
    window.addEventListener("scroll", position, true);
    return () => {
      window.removeEventListener("resize", position);
      window.removeEventListener("scroll", position, true);
    };
  }, [open, position]);

  return <>
    <button ref={button} type="button" className="overlay-settings-button" title={`${label} settings`}
      aria-label={`${label} settings`} aria-expanded={open} aria-haspopup="dialog"
      popoverTarget={id} onClick={position}
      onKeyDown={event => { if ([" ", "Enter", "Escape"].includes(event.key)) event.stopPropagation(); }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M9.5 3h5l.6 2.5 1.5.9 2.5-.7 2.5 4.3-1.9 1.8v1.8l1.9 1.8-2.5 4.3-2.5-.7-1.5.9-.6 2.5h-5l-.6-2.5-1.5-.9-2.5.7-2.5-4.3 1.9-1.8v-1.8L2.5 10 5 5.7l2.5.7 1.5-.9L9.5 3Z" />
        <circle cx="12" cy="12.7" r="3.2" />
      </svg>
    </button>
    <div ref={panel} id={id} popover="auto" className="overlay-settings-panel" role="dialog"
      aria-label={`${label} settings`} onToggle={event => setOpen(event.currentTarget.matches(":popover-open"))}
      onKeyDown={event => event.stopPropagation()}>
      <strong className="overlay-settings-title">{label}</strong>
      {children}
    </div>
  </>;
}
